/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Box,
  Breadcrumbs,
  Chip,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  InputAdornment,
  Popover,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import api from "../../api";
import { Query_Keys } from "../../lib/queryKeys";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { IndexTable } from "../../components/index_table";
import { Loader } from "../../components/loader";
import { ModalCancelButton } from "../../components/modal_cancel_button";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { PermissionProfileModal } from "../../components/permission_profile_modal";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { permissionProfileMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/permissionProfileMutationKeys";
import { usePermissionModules } from "../../hooks/usePermissionModules";
import { useReportExport } from "../../hooks/useReportExport";
import { useDebounce } from "../../utils/useDebounce";
import { checkAdmin } from "../../utils/checkAdmin";
import { resetPageAfterDeletion } from "../../utils/resetPageAfterDeletion";
import { CSV_EXPORT_PATH, withApiQuery } from "../../utils/reportExport";
import {
  StyledActionsContainer,
  StyledBreadCrumbContainer,
  StyledCreateButton,
  StyledDialogBody,
  StyledDialogTitle,
  StyledHeaderRow,
  StyledLink,
  StyledSearchBar,
  StyledTitle,
  StyledTitleContainer,
} from "./styles";

const ROLE_LABELS = {
  admin: "Instance Admin",
  viewer: "Instance Viewer",
};

const roleLabel = (role) => ROLE_LABELS[role] || role;

const MODULES_POPOVER_MAX_HEIGHT = "220px";

const MODULE_CHIP_SX = {
  backgroundColor: "#edf2f7",
  color: "#64748b",
  fontWeight: 500,
  height: "28px",
  minWidth: "64px",
  maxWidth: "100%",
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    px: 1.5,
  },
};

const MODULE_ROW_SX = {
  alignItems: "center",
  boxSizing: "border-box",
  display: "flex",
  px: 2,
  py: 1.25,
  width: "100%",
};

// Memoised: a page holds up to 100 of these, each owning a MUI Popover, and they must not be
// rebuilt on every keystroke in the search box.
const ModulesCell = React.memo(({ moduleKeys, moduleLabelMap, profileName }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimer = useRef(null);
  const count = moduleKeys?.length || 0;

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const openPopover = (event) => {
    if (!count) return;
    clearTimeout(closeTimer.current);
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const closePopover = () => {
    closeTimer.current = setTimeout(() => setIsOpen(false), 150);
  };

  const keepOpen = () => {
    clearTimeout(closeTimer.current);
    setIsOpen(true);
  };

  const open = Boolean(anchorEl) && isOpen;

  return (
    <Box
      data-testid="permission-profile-module-count"
      onMouseEnter={openPopover}
      onMouseLeave={closePopover}
      onFocus={openPopover}
      onBlur={closePopover}
      tabIndex={count ? 0 : -1}
      role={count ? "button" : undefined}
      aria-haspopup={count ? "true" : undefined}
      aria-expanded={count ? open : undefined}
      sx={{ display: "inline-flex", alignItems: "center" }}
    >
      <Chip
        size="small"
        label={`${count} ${count === 1 ? "Module" : "Modules"}`}
        sx={{
          backgroundColor: "white",
          "&:hover": { backgroundColor: "white" },
          height: "23px",
          border: "2px solid lightgray",
          cursor: count ? "pointer" : "default",
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            "data-testid": "permission-profile-modules-popover",
            onMouseEnter: keepOpen,
            onMouseLeave: closePopover,
            sx: { minWidth: "200px", borderRadius: "8px", overflow: "hidden", marginTop: "5px" },
          },
        }}
        disableRestoreFocus
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#1f2937" }}>Modules</Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>{profileName}</Typography>
        </Box>

        <Divider />

        <Box
          data-testid="permission-profile-modules-list"
          sx={{ maxHeight: MODULES_POPOVER_MAX_HEIGHT, overflowY: "auto", width: "100%" }}
        >
          {moduleKeys.map((key, index) => (
            <Box key={key}>
              <Box data-testid={`permission-profile-modules-row-${key}`} sx={MODULE_ROW_SX}>
                <Chip size="small" label={moduleLabelMap[key] || key} sx={MODULE_CHIP_SX} />
              </Box>
              {index < moduleKeys.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      </Popover>
    </Box>
  );
});

ModulesCell.displayName = "ModulesCell";

export const PermissionProfiles = () => {
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 300);
  const [modalProfile, setModalProfile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saveErrors, setSaveErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [blockedDeleteTarget, setBlockedDeleteTarget] = useState(null);
  // Hold the last non-null target separately from the one driving `open`, so the dialog's own
  // close transition still has a real name/count to render instead of flashing "undefined" once
  // the target that closed it has already been cleared.
  const [lastDeleteTarget, setLastDeleteTarget] = useState(null);
  const [lastBlockedDeleteTarget, setLastBlockedDeleteTarget] = useState(null);
  const { exporting: csvLoading, runExport } = useReportExport();

  useEffect(() => {
    if (deleteTarget) setLastDeleteTarget(deleteTarget);
  }, [deleteTarget]);

  useEffect(() => {
    if (blockedDeleteTarget) setLastBlockedDeleteTarget(blockedDeleteTarget);
  }, [blockedDeleteTarget]);

  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles);
  const isInstanceLevel = !(superAdmin?.role === "admin" || superAdmin?.role === "viewer");
  const canManage = superAdmin?.role === "admin";

  const { groups } = usePermissionModules({ enabled: !isInstanceLevel });
  const moduleLabelMap = useMemo(() => {
    const map = {};
    groups.forEach((group) => {
      (group?.modules || []).forEach((module) => {
        if (module?.key) map[module.key] = module.label;
      });
    });
    return map;
  }, [groups]);

  const fetchPermissionProfiles = async () => {
    const response = await api.get("/v1/admin/permission_profiles", {
      params: {
        per_page: itemsPerPage,
        page,
        search: debouncedSearchTerm,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      },
    });

    return { data: response?.data, pageCount: response?.headers["total-count"] };
  };

  const { data: profilesData, isFetching, isLoading, isError } = useQuery({
    queryKey: Query_Keys.permission_profiles({
      page,
      itemsPerPage,
      search: debouncedSearchTerm,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchPermissionProfiles,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: !isInstanceLevel,
  });

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalProfile(null);
    setSaveErrors({});
  }, []);

  const { mutate: saveProfile, isPending: isSaving } = useOtMutation({
    mutationKey: ["permission_profile_save"],
    mutationFn: ({ id, data }) =>
      id ? api.put(`/v1/admin/permission_profiles/${id}`, data) : api.post("/v1/admin/permission_profiles", data),
    invalidateKeys: permissionProfileMutationKeyGroups.update,
    successMessage: ({ variables }) =>
      variables?.id
        ? "Permission profile has been updated successfully!"
        : "Permission profile has been created successfully!",
    onSuccess: closeModal,
    onError: (error) => setSaveErrors(error?.response?.data?.errors || {}),
  });

  const { mutate: deleteProfile, isPending: isDeleting } = useOtMutation({
    mutationKey: ["permission_profile_delete"],
    mutationFn: (id) => api.delete(`/v1/admin/permission_profiles/${id}`),
    invalidateKeys: permissionProfileMutationKeyGroups.delete,
    successMessage: "Permission profile has been deleted successfully!",
    onSuccess: () => {
      resetPageAfterDeletion({
        totalCount: profilesData?.pageCount,
        removed: 1,
        page,
        itemsPerPage,
        setPage,
      });
    },
    onSettled: () => setDeleteTarget(null),
  });

  const handleOpenModal = useCallback((profile = null) => {
    setSaveErrors({});
    setModalProfile(profile);
    setModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((profile) => {
    if (profile?.assigned_users_count > 0) {
      setBlockedDeleteTarget(profile);
      return;
    }

    setDeleteTarget(profile);
  }, []);

  // Memoised on the data and the two things the cells read, so typing in the search box does
  // not rebuild a page of chips and popovers before the debounce has even fired.
  const rows = useMemo(
    () =>
      profilesData?.data?.map((profile) => ({
        id: profile.id,
        name: profile.name,
        description: profile.description || "-",
        applicable_roles: (
          <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
            {(profile.applicable_roles || []).map((role) => (
              <Chip key={role} size="small" label={roleLabel(role)} sx={{ backgroundColor: "#e9f0f9" }} />
            ))}
          </Box>
        ),
        modules: (
          <ModulesCell moduleKeys={profile.module_keys || []} moduleLabelMap={moduleLabelMap} profileName={profile.name} />
        ),
        assigned_users: profile.assigned_users_count,
        active: profile.active ? "Active" : "Inactive",
        actions: (
          <StyledActionsContainer>
            <Tooltip title={canManage ? "Edit" : "View"} arrow>
              <IconButton
                data-testid="permission-profile-edit-button"
                aria-label={`${canManage ? "Edit" : "View"} ${profile.name}`}
                onClick={() => handleOpenModal(profile)}
              >
                {canManage ? (
                  <ModeEditIcon sx={{ color: "#707070", cursor: "pointer" }} />
                ) : (
                  <VisibilityIcon sx={{ color: "#707070", cursor: "pointer" }} />
                )}
              </IconButton>
            </Tooltip>

            {canManage && (
              <Tooltip title="Delete" arrow>
                <IconButton
                  data-testid="permission-profile-delete-button"
                  aria-label={`Delete ${profile.name}`}
                  onClick={() => handleDeleteClick(profile)}
                >
                  <DeleteIcon sx={{ color: "#707070", cursor: "pointer" }} />
                </IconButton>
              </Tooltip>
            )}
          </StyledActionsContainer>
        ),
      })),
    [profilesData, moduleLabelMap, canManage, handleOpenModal, handleDeleteClick]
  );

  if (admin && isInstanceLevel) {
    return <Navigate to="/admin/no_permissions" replace />;
  }

  const columns = [
    { key: "id", label: "ID", sortable: true },
    { key: "name", label: "Profile Name", sortable: true },
    { key: "description", label: "Description", sortable: false },
    { key: "applicable_roles", label: "Applicable Roles", sortable: false },
    { key: "modules", label: "Modules", sortable: false },
    { key: "assigned_users", label: "Assigned Users", sortable: false },
    { key: "active", label: "Status", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  const handleSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  // Exports every profile matching the current search and sort, not just this page.
  const handleExportCsv = () =>
    runExport({
      url: withApiQuery(CSV_EXPORT_PATH, {
        report: "permission_profiles",
        search: debouncedSearchTerm || undefined,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      }),
      fallbackFilename: `permission_profiles_${dayjs().format("YYYY-MM-DD")}.csv`,
      format: "csv",
    });

  return (
    <Box>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color={"#515152"}>Permission Profiles</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <StyledHeaderRow>
        <StyledTitleContainer>
          <StyledTitle>Permission Profiles</StyledTitle>
        </StyledTitleContainer>

        <StyledSearchBar
          value={search}
          placeholder="Search profiles"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          onChange={(event) => handleSearch(event.target.value)}
          autoComplete="off"
        />
      </StyledHeaderRow>

      <Divider
        style={{ borderStyle: "solid", borderWidth: "1px", marginBottom: canManage ? "0px" : "16px" }}
      />

      {canManage && (
        <Box sx={{ padding: "20px 0px" }}>
          <StyledCreateButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal(null)}
            data-testid="permission-profile-create-btn"
          >
            Create Profile
          </StyledCreateButton>
        </Box>
      )}

      {!admin || ((isFetching || isLoading) && !debouncedSearchTerm) ? (
        // `!admin` covers the window before `/me` resolves: `ModuleRoute` only loaders
        // module-gated routes, and this one is role-gated, so without this the page would
        // otherwise flash a false empty state (`isInstanceLevel` defaults to true, disabling the
        // query, so `isFetching`/`isLoading` never turn on) before the real role is known.
        <Loader />
      ) : isError ? (
        <Alert severity="error" data-testid="permission-profiles-error-message">
          Could not load permission profiles. Please retry.
        </Alert>
      ) : (
        <IndexTable
          report={false}
          columns={columns}
          data={rows}
          tableId="permission-profiles"
          type="entries"
          sortMode="server"
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          pageCount={profilesData?.pageCount}
          searchLoading={isFetching && !!debouncedSearchTerm}
          hasSearchTerm={!!debouncedSearchTerm}
          emptyStateText="No permission profiles found"
          showDownloadButton
          disableDownloadButton={!profilesData?.data?.length}
          handleExportCsv={handleExportCsv}
          csvLoading={csvLoading}
        />
      )}

      {modalOpen && (
        <PermissionProfileModal
          open
          profile={modalProfile}
          readOnly={!canManage}
          isSubmitting={isSaving}
          serverErrors={saveErrors}
          onClose={closeModal}
          onSubmit={(data) => saveProfile({ id: modalProfile?.id, data })}
        />
      )}

      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        Icon={CheckIcon}
        dialogTitle="Are you sure you want to delete this permission profile?"
        dialogBody={
          <>
            This action will permanently delete
            <span style={{ fontWeight: "600" }}>{` '${lastDeleteTarget?.name}'`}</span>
          </>
        }
        confirmationText="Yes, I want to delete the permission profile"
        handleConfirm={() => deleteProfile(deleteTarget?.id)}
        isLoading={isDeleting}
        dataTestId="permission-profile-delete-dialog"
      />

      <Dialog
        open={Boolean(blockedDeleteTarget)}
        onClose={() => setBlockedDeleteTarget(null)}
        data-testid="permission-profile-blocked-delete-dialog"
        maxWidth="xs"
        fullWidth
      >
        <StyledDialogTitle>This profile cannot be deleted</StyledDialogTitle>
        <StyledDialogBody>
          {`'${lastBlockedDeleteTarget?.name}' is assigned to ${lastBlockedDeleteTarget?.assigned_users_count} ${
            lastBlockedDeleteTarget?.assigned_users_count === 1 ? "user" : "users"
          }. Move those users onto another profile, or deactivate this one instead.`}
        </StyledDialogBody>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <ModalCancelButton
            disableRipple
            onClick={() => setBlockedDeleteTarget(null)}
            data-testid="permission-profile-blocked-delete-close-btn"
          >
            Close
          </ModalCancelButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
